import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { FieldTextInput, ValidationError } from '../../components';

import css from './StripePaymentAddress.module.css';

const COUNTRY_SELECT_MAX_HEIGHT = 240;

/**
 * Scrollable country dropdown — native <select> cannot honor max-height.
 *
 * @component
 */
const CountrySelectField = props => {
  const {
    id,
    label,
    placeholder,
    disabled,
    input,
    meta,
    options,
    maxHeight = COUNTRY_SELECT_MAX_HEIGHT,
  } = props;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const rootRef = useRef(null);
  const selectedOption = options.find(
    option =>
      String(option.value).toLowerCase() === String(input.value).toLowerCase() ||
      String(option.label).toLowerCase() === String(input.value).toLowerCase()
  );
  const hasError = meta.touched && meta.error;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setSearchQuery('');
    const handlePointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const handleSelect = value => {
    input.onChange(value);
    input.onBlur();
    setOpen(false);
  };

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  return (
    <div className={classNames(css.field, css.countryField)} ref={rootRef}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      <button
        id={id}
        type="button"
        className={classNames(css.countrySelectTrigger, {
          [css.countrySelectTriggerError]: hasError,
          [css.countrySelectTriggerOpen]: open,
        })}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) {
            setOpen(prev => !prev);
          }
        }}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
      >
        <span
          className={selectedOption ? css.countrySelectValue : css.countrySelectPlaceholder}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={classNames(css.countrySelectChevron, {
            [css.countrySelectChevronOpen]: open,
          })}
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8 10L12 14L16 10"
            stroke="#888888"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div className={css.countrySelectPanel} style={{ maxHeight }}>
          <div className={css.countrySearchWrapper}>
            <svg
              className={css.countrySearchIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255, 255, 255, 0.6)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className={css.countrySearchInput}
              placeholder="Search country..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            {searchQuery ? (
              <button
                type="button"
                className={css.countrySearchClear}
                onClick={e => {
                  e.stopPropagation();
                  setSearchQuery('');
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              >
                &times;
              </button>
            ) : null}
          </div>
          <ul className={css.countryOptionsList} role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => {
                const isSelected = option.value === input.value;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className={classNames(css.countrySelectOption, {
                      [css.countrySelectOptionActive]: isSelected,
                    })}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </li>
                );
              })
            ) : (
              <li className={css.countrySelectNoResults}>No countries found</li>
            )}
          </ul>
        </div>
      ) : null}

      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * A component that renders a Stripe payment address.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {intlShape} props.intl - The intl object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {Object} props.form - The form object
 * @param {string} props.fieldId - The field id
 * @param {Object} props.card - The card object
 * @param {string} props.locale - The locale
 * @returns {JSX.Element}
 */
const StripePaymentAddress = props => {
  const { className, intl, disabled, form, fieldId, card, locale } = props;

  const optionalText = intl.formatMessage({
    id: 'StripePaymentAddress.optionalText',
  });

  const addressLine1Label = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Label',
  });
  const addressLine1Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Placeholder',
  });
  const addressLine1Required = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.addressLine1Required',
    })
  );

  const addressLine2Label = intl.formatMessage(
    { id: 'StripePaymentAddress.addressLine2Label' },
    { optionalText: optionalText }
  );

  const addressLine2Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine2Placeholder',
  });

  const postalCodeLabel = intl.formatMessage({ id: 'StripePaymentAddress.postalCodeLabel' });
  const postalCodePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.postalCodePlaceholder',
  });
  const postalCodeRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.postalCodeRequired',
    })
  );

  const cityLabel = intl.formatMessage({ id: 'StripePaymentAddress.cityLabel' });
  const cityPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.cityPlaceholder' });
  const cityRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.cityRequired',
    })
  );

  const stateLabel = intl.formatMessage(
    { id: 'StripePaymentAddress.stateLabel' },
    { optionalText: optionalText }
  );
  const statePlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.statePlaceholder' });

  const countryLabel = intl.formatMessage({ id: 'StripePaymentAddress.countryLabel' });
  const countryPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.countryPlaceholder' });
  const countryRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.countryRequired',
    })
  );

  const handleOnChange = event => {
    const value = event.target.value;
    form.change('postal', value);
    if (card && typeof card.update === 'function') {
      card.update({ value: { postalCode: value } });
    }
  };

  // Use the language set in config.localization.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(locale);
  const countryOptions = countryCodes.map(country => ({
    value: country.code,
    label: country.name,
  }));

  return (
    <div className={className ? className : css.root}>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.addressLine1`}
          name="addressLine1"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-line1"
          label={addressLine1Label}
          placeholder={addressLine1Placeholder}
          validate={addressLine1Required}
          onUnmount={() => form.change('addressLine1', undefined)}
        />

        <FieldTextInput
          id={`${fieldId}.addressLine2`}
          name="addressLine2"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-line2"
          label={addressLine2Label}
          placeholder={addressLine2Placeholder}
          onUnmount={() => form.change('addressLine2', undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.postalCode`}
          name="postal"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing postal-code"
          label={postalCodeLabel}
          placeholder={postalCodePlaceholder}
          validate={postalCodeRequired}
          onUnmount={() => form.change('postal', undefined)}
          onChange={event => handleOnChange(event)}
        />

        <FieldTextInput
          id={`${fieldId}.city`}
          name="city"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level2"
          label={cityLabel}
          placeholder={cityPlaceholder}
          validate={cityRequired}
          onUnmount={() => form.change('city', undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.state`}
          name="state"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level1"
          label={stateLabel}
          placeholder={statePlaceholder}
          onUnmount={() => form.change('state', undefined)}
        />

        <Field
          name="country"
          id={`${fieldId}.country`}
          label={countryLabel}
          placeholder={countryPlaceholder}
          disabled={disabled}
          options={countryOptions}
          maxHeight={COUNTRY_SELECT_MAX_HEIGHT}
          validate={countryRequired}
          component={CountrySelectField}
        />
      </div>
    </div>
  );
};

export default StripePaymentAddress;
